import { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import type { HandoffPlatform } from '@usacsmideo/types';
import { consentCopyFor, type HandoffIntent } from './handoffConsentCopy';

/**
 * One-time-per-viewer consent dialog for the external maps handoff
 * (CU-86ajd3yt0, epic CU-86ajd302x).
 *
 * Built on Headless UI's `Dialog` — the repo's established modal pattern (see
 * SimpleDialog / ConsentGateModal) — which provides the fixed overlay, backdrop,
 * focus trap, and portal-to-body. (The earlier hand-rolled version leaned on the
 * un-styled local `Modal`, which has no positioning, so it inserted a full-height
 * block inline in the card instead of overlaying — the "giant div" bug.)
 *
 * The handoff is the single third-party PHI-boundary crossing in the feature:
 * the coordinate leaves MIDEO to Apple/Google/OpenStreetMap tied to the viewer.
 * The maps app/site shows a single pin and CANNOT display our accuracy circle,
 * so this dialog is the last place we can set the expectation that it's an
 * APPROXIMATE area, not an exact spot — the verbatim copy (in handoffConsentCopy)
 * carries that framing, which must not be dropped.
 *
 * "Don't ask again" is OPT-IN — the checkbox is NOT default-checked. One-time
 * per viewer is the ceiling; there is no per-tap modal.
 */
export function HandoffConsentDialog({
  isOpen,
  platform,
  intent = 'view',
  onConfirm,
  onCancel,
  isRecording = false,
}: {
  isOpen: boolean;
  platform: HandoffPlatform;
  /** Which action is being confirmed — selects the copy variant. */
  intent?: HandoffIntent;
  /** Called with the viewer's "don't ask again" choice on confirm. */
  onConfirm: (_dontAskAgain: boolean) => void;
  onCancel: () => void;
  /** True while the consent ack is being recorded (disables the buttons). */
  isRecording?: boolean;
}) {
  const [dontAskAgain, setDontAskAgain] = useState(false);

  return (
    <Dialog
      open={isOpen}
      // Backdrop/Escape close === cancel, but don't let it fire mid-record.
      onClose={() => {
        if (!isRecording) onCancel();
      }}
      className="relative z-[60]"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
      />

      <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-5 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in sm:data-closed:translate-y-0 sm:data-closed:scale-95"
          >
            <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
              Continue to maps?
            </DialogTitle>
            <p className="mt-2 text-sm text-gray-600">{consentCopyFor(platform, intent)}</p>

            <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={dontAskAgain}
                onChange={(e) => setDontAskAgain(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Don&apos;t ask again
            </label>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={isRecording}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => onConfirm(dontAskAgain)}
                disabled={isRecording}
                aria-busy={isRecording}
                className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
              >
                {isRecording ? 'Opening…' : 'Continue'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default HandoffConsentDialog;
